import { API_ENDPOINTS } from "../constants/apiconfig/apiconfig";
import { CONNECT_ENDPOINTS } from "./endpoints";

// Portal-specific storage keys, so this login never collides with the
// main CIODS / ProteoArk authentication.
const ACCESS_KEY = "ciodsconnect_access_token";
const REFRESH_KEY = "ciodsconnect_refresh_token";

// Fired when the session can no longer be refreshed (see AuthContext).
export const AUTH_EXPIRED_EVENT = "ciodsconnect:auth-expired";

// The signed-in user's profile (role, designation, ...) shared by the layout,
// the dashboards and the route guards. Cleared together with the tokens.
const USER_KEY = "ciodsconnect_user";
export const USER_UPDATED_EVENT = "ciodsconnect:user-updated";

export const userStore = {
  get() {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || "{}") || {};
    } catch {
      return {};
    }
  },
  set(user) {
    try {
      localStorage.setItem(USER_KEY, JSON.stringify(user || {}));
    } catch {
      /* storage unavailable: the profile is simply re-fetched */
    }
    window.dispatchEvent(new Event(USER_UPDATED_EVENT));
  },
  clear() {
    try {
      localStorage.removeItem(USER_KEY);
    } catch {
      /* ignore */
    }
  },
};

export const tokenStore = {
  getAccess: () => localStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set(access, refresh) {
    const previousAccess = localStorage.getItem(ACCESS_KEY);
    if (access && previousAccess && previousAccess !== access) clearApiCache();
    if (access) localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    clearApiCache();
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    userStore.clear();
  },
};

// API_ENDPOINTS values are absolute URLs; relative paths use the shared base.
function resolveUrl(url) {
  if (!url) throw new Error("This page is missing its API endpoint.");
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ENDPOINTS.BASE_URL}${url.replace(/^\/+/, "")}`;
}

// Turn DRF errors ({detail} or {field: ["msg"]}) into one readable string.
function extractMessage(data, status) {
  const fallback = `Request failed (${status})`;
  if (!data || typeof data !== "object") return fallback;

  for (const key of ["detail", "error", "message"]) {
    if (typeof data[key] === "string") return data[key];
  }

  const parts = [];
  for (const [field, value] of Object.entries(data)) {
    const text = Array.isArray(value)
      ? value.join(" ")
      : typeof value === "string"
      ? value
      : "";
    if (!text) continue;
    parts.push(
      field === "non_field_errors"
        ? text
        : `${field.replace(/_/g, " ")}: ${text}`
    );
  }
  return parts.join(" ") || fallback;
}

function send(url, fetchOptions, headers, token) {
  return fetch(resolveUrl(url), {
    ...fetchOptions,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  });
}

// One shared refresh call, even if several requests fail at once.
let refreshPromise = null;

// In-memory API response cache. This survives page/tab navigation while the
// SPA remains open, but is cleared when the authenticated account changes or
// signs out. Successful mutations invalidate cached GET responses.
const API_CACHE_TTL_MS = 2 * 60 * 1000;
const API_CACHE_MAX_ENTRIES = 150;
const apiResponseCache = new Map();
const pendingGetRequests = new Map();

export function clearApiCache() {
  apiResponseCache.clear();
  pendingGetRequests.clear();
}

export function invalidateApiCache(urlPrefix = null) {
  if (!urlPrefix) {
    clearApiCache();
    return;
  }
  for (const key of apiResponseCache.keys()) {
    if (key.includes(`|${urlPrefix}`)) apiResponseCache.delete(key);
  }
  for (const key of pendingGetRequests.keys()) {
    if (key.includes(`|${urlPrefix}`)) pendingGetRequests.delete(key);
  }
}

function cacheKey(url, token) {
  // The token is only held in this in-memory key; it is never written to storage.
  return `${token || "anonymous"}|${resolveUrl(url)}`;
}

function readCachedResponse(key) {
  const entry = apiResponseCache.get(key);
  if (!entry) return { hit: false };
  if (Date.now() >= entry.expiresAt) {
    apiResponseCache.delete(key);
    return { hit: false };
  }
  // Refresh LRU order so frequently used responses remain in the cache.
  apiResponseCache.delete(key);
  apiResponseCache.set(key, entry);
  return { hit: true, value: entry.value };
}

function writeCachedResponse(key, value, ttl = API_CACHE_TTL_MS) {
  if (ttl <= 0) return;
  apiResponseCache.delete(key);
  apiResponseCache.set(key, { value, expiresAt: Date.now() + ttl });
  while (apiResponseCache.size > API_CACHE_MAX_ENTRIES) {
    apiResponseCache.delete(apiResponseCache.keys().next().value);
  }
}

function refreshAccessToken() {
  const refresh = tokenStore.getRefresh();
  if (!refresh) return Promise.reject(new Error("No refresh token"));

  if (!refreshPromise) {
    refreshPromise = fetch(resolveUrl(CONNECT_ENDPOINTS.refresh), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh }),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.access) throw new Error("Refresh failed");
        tokenStore.set(data.access, data.refresh);
        return data.access;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function expireSession() {
  tokenStore.clear();
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
  return new Error("Your session has expired. Please sign in again.");
}

/**
 * options.auth (default true): attach the JWT and retry once after a refresh
 * when the server answers 401. Pass auth: false for login/register.
 */
export async function apiRequest(url, options = {}) {
  const {
    auth = true,
    headers,
    cache = true,
    cacheTtl = API_CACHE_TTL_MS,
    ...fetchOptions
  } = options;
  const method = String(fetchOptions.method || "GET").toUpperCase();
  const token = auth ? tokenStore.getAccess() : null;
  const shouldCache = method === "GET" && auth && Boolean(token) && cache !== false;
  const key = shouldCache ? cacheKey(url, token) : null;

  if (shouldCache) {
    const cached = readCachedResponse(key);
    if (cached.hit) return cached.value;
    // If this same GET is already in progress, share it across components.
    if (pendingGetRequests.has(key)) return pendingGetRequests.get(key);
  }

  const requestPromise = (async () => {
    let activeToken = token;
    let response;
    try {
      response = await send(url, fetchOptions, headers, activeToken);

      if (auth && response.status === 401) {
        try {
          activeToken = await refreshAccessToken();
        } catch {
          throw expireSession();
        }
        response = await send(url, fetchOptions, headers, activeToken);
        if (response.status === 401) throw expireSession();
      }
    } catch (err) {
      if (err instanceof TypeError) {
        throw new Error("Unable to reach the server. Please try again.");
      }
      throw err;
    }

    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(extractMessage(data, response.status));

    if (shouldCache) {
      writeCachedResponse(key, data, Number.isFinite(cacheTtl) ? cacheTtl : API_CACHE_TTL_MS);
    } else if (method !== "GET") {
      // Mutations can affect dashboards, directories, teams and research lists.
      clearApiCache();
    }
    return data;
  })();

  if (shouldCache) {
    pendingGetRequests.set(key, requestPromise);
    try {
      return await requestPromise;
    } finally {
      pendingGetRequests.delete(key);
    }
  }
  return requestPromise;
}

export const registerUser = (userData) =>
  apiRequest(CONNECT_ENDPOINTS.register, {
    method: "POST",
    auth: false,
    body: JSON.stringify(userData),
  });

export const loginUser = (credentials) =>
  apiRequest(CONNECT_ENDPOINTS.login, {
    method: "POST",
    auth: false,
    body: JSON.stringify(credentials),
  });

export const adminLoginUser = (credentials) =>
  apiRequest(CONNECT_ENDPOINTS.adminLogin, {
    method: "POST",
    auth: false,
    body: JSON.stringify(credentials),
  });

export const getDashboard = () =>
  apiRequest(CONNECT_ENDPOINTS.dashboard);

export function logoutUser() {
  tokenStore.clear();
}

// Organisation-level dashboard (Admin / Director only).
export const getOrganisationDashboard = () =>
  apiRequest(CONNECT_ENDPOINTS.organisationDashboard);

// Complete publication catalogue for Admin / Director organisation view.
export const getOrganisationPublications = () =>
  apiRequest(CONNECT_ENDPOINTS.organisationPublications);

// Safe organisation-wide aggregate for Faculty: publication total only, never status breakdowns.
export const getFacultyResearchSummary = () =>
  apiRequest(CONNECT_ENDPOINTS.facultyResearchSummary);


// Account management and password-security endpoints. Derive the portal API
// prefix from the existing dashboard endpoint to keep the configured API base.
const portalEndpoint = (suffix) =>
  String(CONNECT_ENDPOINTS.dashboard).replace(/dashboard\/?$/, suffix.replace(/^\/+/, ""));

export const changeOwnPassword = (payload) =>
  apiRequest(portalEndpoint("auth/change-password/"), {
    method: "POST",
    body: JSON.stringify(payload),
    cache: false,
  });

export const getManagedAccounts = (params = {}) => {
  const query = new URLSearchParams();
  if (params.search) query.set("search", params.search);
  if (params.role) query.set("role", params.role);
  if (params.active !== undefined && params.active !== "") query.set("active", params.active);
  const suffix = `management/accounts/${query.toString() ? `?${query.toString()}` : ""}`;
  return apiRequest(portalEndpoint(suffix), { cache: false });
};

export const createManagedAccount = (payload) =>
  apiRequest(portalEndpoint("management/accounts/"), {
    method: "POST",
    body: JSON.stringify(payload),
    cache: false,
  });

export const updateManagedAccount = (userId, payload) =>
  apiRequest(portalEndpoint(`management/accounts/${encodeURIComponent(userId)}/`), {
    method: "PATCH",
    body: JSON.stringify(payload),
    cache: false,
  });

export const resetManagedAccountPassword = (userId) =>
  apiRequest(portalEndpoint(`management/accounts/${encodeURIComponent(userId)}/reset-password/`), {
    method: "POST",
    body: JSON.stringify({}),
    cache: false,
  });
